// Opportunities dashboard (Business Development → Opportunities), which replaces the Power BI report.

export type OpportunityDashboardView = "funnel" | "opportunities" | "canceled" | "lost" | "won"

export type OpportunityOutcomeType = "canceled" | "lost" | "won"

export interface OpportunityDashboardFilters {
    fiscalYear: number
    divisionIds: string[]
    departmentIds: string[]
    branchIds: string[]
    // Fiscal periods of the close date (1 = September … 12 = August)
    months: number[]
    // Text contained in the opportunity description
    search: string
}

export interface OpportunityDashboardSearchFields {
    divisions: { id: string; description: string }[]
    departments: { id: string; description: string; parentId: string | null }[]
    branches: { id: string; name: string }[]
}

export interface StageSummary {
    stage: string
    value: number
    count: number
}

export interface FunnelStage extends StageSummary {
    // Win probability shown next to the stage name (0–1)
    weight: number
}

export interface GroupSummary {
    name: string
    value: number
    count: number
}

export interface OpportunityListRow {
    id: string
    description: string
    stage: string
    owner: string | null
    client: string | null
    branch: string | null
    division: string | null
    department: string | null
    closeDate: string | null
    resultDate: string | null
    value: number
}

export interface FunnelZone {
    value: number
    count: number
    // null: no goal (green zone = won)
    goal: number | null
}

export interface FunnelRevenueResult {
    fiscalYear: number
    zones: { blue: FunnelZone; yellow: FunnelZone; green: FunnelZone }
    funnel: FunnelStage[]
    revenue: {
        fyRevenue: number
        revenueTarget: number
        gap: number
        totalFunnel: number
        earnedYtd: number
        // Packed YYYYPP of the last closed period counted in earnedYtd; null when none has closed
        earnedThroughPeriod: number | null
        workInHandRemaining: number
        nextFiscalYear: number
        carryover: number
    }
}

export interface OpportunitiesResult {
    stages: StageSummary[]
    won: StageSummary[]
    rows: OpportunityListRow[]
    funnel: FunnelStage[]
}

export interface OutcomeResult {
    stage: string
    totalValue: number
    totalCount: number
    outcomeValue: number
    outcomeCount: number
    valueRatio: number | null
    countRatio: number | null
    stages: StageSummary[]
    rows: OpportunityListRow[]
    byDivision: GroupSummary[]
    byDepartment: GroupSummary[]
}

export interface OpportunityDashboardViews {
    funnel?: FunnelRevenueResult | null
    opportunities?: OpportunitiesResult | null
    canceled?: OutcomeResult | null
    lost?: OutcomeResult | null
    won?: OutcomeResult | null
}

// Tab label for each view, in display order
export const OPPORTUNITY_DASHBOARD_TABS: Record<OpportunityDashboardView, string> = {
    funnel: "Funnel + Revenue",
    opportunities: "Opportunities",
    canceled: "Process Canceled Report",
    lost: "Lost Report",
    won: "Won Report",
}

export const viewFromTab = (tab: string): OpportunityDashboardView =>
    (Object.keys(OPPORTUNITY_DASHBOARD_TABS) as OpportunityDashboardView[])
        .find(v => OPPORTUNITY_DASHBOARD_TABS[v] === tab) ?? "funnel"

// Cache key for a view's data: a tab reloads only when the filters change.
export const filtersKey = (f: OpportunityDashboardFilters): string =>
    [f.fiscalYear, f.divisionIds.join(","), f.departmentIds.join(","), f.branchIds.join(","), f.months.join(","), f.search].join("|")
